/**
 * «Entrar con Google» de los portales CLIENTE y PARTNER (popup + sondeo).
 *
 * Réplica del flujo de la SPA de plataforma (frontend-react LoginPage): el
 * backend delega el consentimiento en Composio a través del auth-service global,
 * así que aquí solo hay tres pasos —arrancar, abrir el popup y sondear— y ni una
 * sola credencial de Google.
 *
 * Por qué SONDEO y no `postMessage`: Composio no siempre devuelve el popup a la
 * callback del auth-service, así que esperar un mensaje del popup dejaría el
 * login colgado en un porcentaje de los intentos. La señal fiable la tiene el
 * backend (la conexión de Composio pasa a ACTIVE), y la publica respondiendo 425
 * GOOGLE_LOGIN_PENDING mientras no ha pasado. Se reintenta hasta el plazo
 * máximo; cualquier otro error corta de inmediato (no es «aún no», es «no»).
 *
 * Vive en `utils` y no en un cliente HTTP porque los dos portales tienen
 * clientes distintos (fetch el de cliente, axios el de partner) y este flujo
 * tiene que ser IDÉNTICO en los dos: mismos códigos, mismos tiempos, mismo
 * mensaje de error.
 */
import i18next from 'i18next';
import { PortalLoginError, portalLoginErrorFromFetch } from './portalAuthErrors';

export type PortalKind = 'customer' | 'partner';

/** Código con el que el backend dice «el consentimiento sigue abierto». */
export const GOOGLE_LOGIN_PENDING = 'GOOGLE_LOGIN_PENDING';

// Plazo máximo de un intento y separación entre sondeos. 3 minutos es la red de
// seguridad para que un popup abandonado no deje el botón girando para siempre.
const POLL_INTERVAL_MS = 2500;
const DEADLINE_MS = 180_000;

interface GoogleStartResponse {
  redirect_url: string;
  login_ref: string;
}

const base = (portal: PortalKind) => `/api/v1/${portal}/auth/google`;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** GET …/google/start — URL de consentimiento + referencia opaca del intento. */
export async function googleStart(
  portal: PortalKind,
  returnOrigin: string
): Promise<GoogleStartResponse> {
  let response: Response;
  try {
    response = await fetch(
      `${base(portal)}/start?return_origin=${encodeURIComponent(returnOrigin)}`
    );
  } catch {
    throw new PortalLoginError(
      i18next.t('portalAuth.connection', 'Error de conexión. Revisa tu conexión a internet.')
    );
  }
  if (!response.ok) throw await portalLoginErrorFromFetch(response);
  return (await response.json()) as GoogleStartResponse;
}

/**
 * POST …/google/complete — canjea la referencia por el token del portal.
 * Devuelve el cuerpo tal cual (cada portal sabe qué campos suyos leer).
 */
export async function googleComplete<T>(portal: PortalKind, loginRef: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${base(portal)}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login_ref: loginRef }),
    });
  } catch {
    throw new PortalLoginError(
      i18next.t('portalAuth.connection', 'Error de conexión. Revisa tu conexión a internet.')
    );
  }
  if (!response.ok) throw await portalLoginErrorFromFetch(response);
  return (await response.json()) as T;
}

/**
 * Flujo completo: start → popup → sondeo de complete → cuerpo del login.
 *
 * El popup se cierra al terminar (bien o mal) para no dejar una ventana huérfana
 * con una sesión de Google a medias. Si el navegador lo bloquea se lanza un error
 * que lo DICE: sin eso el usuario ve un botón que no hace nada.
 */
export async function loginWithGoogle<T>(portal: PortalKind): Promise<T> {
  const { redirect_url, login_ref } = await googleStart(portal, window.location.origin);

  const popup = window.open(
    redirect_url,
    `wai-google-login-${portal}`,
    'width=500,height=680,menubar=no,toolbar=no,location=no,status=no'
  );
  if (!popup) {
    throw new PortalLoginError(
      i18next.t(
        'portalAuth.googlePopupBlocked',
        'El navegador ha bloqueado la ventana emergente. Permítela e inténtalo de nuevo.'
      )
    );
  }

  try {
    const deadline = Date.now() + DEADLINE_MS;
    while (Date.now() < deadline) {
      await sleep(POLL_INTERVAL_MS);
      try {
        return await googleComplete<T>(portal, login_ref);
      } catch (err) {
        // «Aún no ha terminado» → seguir sondeando. Cualquier otro error
        // (usuario no habilitado, cuenta bloqueada, Composio caído) es final.
        if (err instanceof PortalLoginError && err.code === GOOGLE_LOGIN_PENDING) continue;
        throw err;
      }
    }
    throw new PortalLoginError(
      i18next.t(
        'portalAuth.googleTimeout',
        'El acceso con Google no se completó a tiempo. Inténtalo de nuevo.'
      )
    );
  } finally {
    try {
      if (!popup.closed) popup.close();
    } catch {
      /* el popup ya navegó a otro origen: no se puede consultar ni cerrar */
    }
  }
}
